import {
  Download,
  Clock,
  CheckCircle,
  AlertCircle,
  LoaderCircle,
  Check,
} from 'lucide-react';
import { useTranslation } from 'react-i18next';

import { Badge } from '@/components/ui/Badge';
import { Button } from '@/components/ui/Button';
import type { CSVFile } from '@/services/CSVService';

interface FileItemProps {
  file: CSVFile;
  onDownloadFile: (file: CSVFile) => void;
  isGenerating?: boolean;
  hasDownloadUrl?: boolean;
}

const getStatusConfig = (t: any) => ({
  PROCESSING: {
    icon: Clock,
    label: t('status.PROCESSING'),
    variant: 'secondary' as const,
    color: 'text-yellow-600',
  },
  PROCESSED: {
    icon: CheckCircle,
    label: t('status.PROCESSED'),
    variant: 'default' as const,
    color: 'text-green-600',
  },
  DONE: {
    icon: CheckCircle,
    label: t('status.DONE'),
    variant: 'default' as const,
    color: 'text-green-600',
  },
  ERROR: {
    icon: AlertCircle,
    label: t('status.ERROR'),
    variant: 'destructive' as const,
    color: 'text-red-600',
  },
});

function formatDate(dateString: string) {
  const date = new Date(dateString);
  return date.toLocaleDateString('pt-BR', {
    day: '2-digit',
    month: '2-digit',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  });
}

export function FileItem({
  file,
  onDownloadFile,
  isGenerating,
  hasDownloadUrl,
}: FileItemProps) {
  const { t } = useTranslation('translation', {
    keyPrefix: 'pages.Settings.components.ProductsManager.components.FileList',
  });

  const statusConfig = getStatusConfig(t);
  const config = statusConfig[file.status] || statusConfig.PROCESSING;
  const Icon = config.icon;

  return (
    <div className="flex items-center justify-between rounded-lg border border-border p-4 transition-colors hover:border-primary/50">
      <div className="flex flex-1 items-center gap-3">
        <Icon className={`h-4 w-4 ${config.color}`} />
        <div className="min-w-0 flex-1">
          <p className="truncate font-medium">{file.originalName}</p>
          <div className="mt-1 flex items-center gap-3">
            <Badge variant={config.variant} className="text-xs">
              {config.label}
            </Badge>
            <Badge variant="outline" className="text-xs">
              {file.type}
            </Badge>
            <span className="text-xs text-muted-foreground">
              {formatDate(file.updatedAt || file.createdAt)}
            </span>
          </div>

          {file.status === 'ERROR' && (
            <small className="mt-1 block text-destructive">
              {file.errorMessage}
            </small>
          )}
        </div>
      </div>

      {file.status === 'DONE' && file.type !== 'SHOP_THE_LOOK' && (
        <>
          {!isGenerating && !hasDownloadUrl && (
            <Button
              size="sm"
              onClick={() => onDownloadFile(file)}
              className="flex items-center gap-1"
            >
              <Download className="h-3 w-3" />
              {t('downloadButton')}
            </Button>
          )}

          {!isGenerating && hasDownloadUrl && (
            <Button
              size="sm"
              onClick={() => onDownloadFile(file)}
              className="flex items-center gap-1 bg-green-600 hover:bg-green-700"
            >
              <Check className="h-3 w-3" />
              {t('downloadReady')}
            </Button>
          )}

          {isGenerating && (
            <Button size="sm" disabled className="flex items-center gap-1">
              <LoaderCircle className="h-3 w-3 animate-spin" />
              {t('generating')}
            </Button>
          )}
        </>
      )}
    </div>
  );
}
